import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, ComposedChart
} from 'recharts';
import {
  FileText, AlertTriangle, Clock, CheckCircle, TrendingUp, 
  Building2, Shield, ChevronRight, Eye, Target, Calendar,
  ArrowUpRight, ArrowDownRight, Users, Briefcase, Scale,
  FileSearch, FileWarning, BarChart3, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = {
  primary: '#1d4ed8',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6'
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-2xl border border-slate-700/50">
        <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-white text-sm">{p.name}: <span className="font-bold">{p.value}</span></p>
        ))}
      </div>
    );
  }
  return null;
};

export const CGEDashboard = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [acts, setActs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, actsRes, logsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/acts'),
        api.get('/audit-logs?limit=100')
      ]);
      
      setStats(statsRes.data);
      setActs(actsRes.data || []);
      setAuditLogs(logsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // CGE-specific metrics
  const complianceRate = stats ? Math.round(((stats.concluidos || 0) / Math.max(stats.total || 1, 1)) * 100) : 0;
  const riskExposure = acts.filter(a => a.status === 'ATRASADO' || a.nivel_risco === 'CRITICO').length;
  const pendingAudits = acts.filter(a => a.tipo_ato === 'AUDITORIA' && a.status !== 'CONCLUIDO').length;

  // Acts by control origin (using control_body field)
  const byOrigin = acts.reduce((acc, act) => {
    const origin = act.control_body || act.orgao_origem || 'Outros';
    acc[origin] = (acc[origin] || 0) + 1;
    return acc;
  }, {});
  const originData = Object.entries(byOrigin).map(([name, value]) => ({ name, value })).slice(0, 6);

  // Monthly trend (last 6 months)
  const monthlyTrend = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    const monthActs = acts.filter(a => {
      const actDate = new Date(a.created_at);
      return actDate.getMonth() === d.getMonth() && actDate.getFullYear() === d.getFullYear();
    });
    return {
      month: d.toLocaleString('pt-BR', { month: 'short' }),
      recebidos: monthActs.length,
      concluidos: monthActs.filter(a => a.status === 'CONCLUIDO').length,
      atrasados: monthActs.filter(a => a.status === 'ATRASADO').length
    };
  });

  // Risk matrix data
  const riskMatrix = [
    { risco: 'Crítico', count: acts.filter(a => a.nivel_risco === 'CRITICO').length },
    { risco: 'Alto', count: acts.filter(a => a.nivel_risco === 'ALTO').length },
    { risco: 'Médio', count: acts.filter(a => a.nivel_risco === 'MEDIO').length },
    { risco: 'Baixo', count: acts.filter(a => a.nivel_risco === 'BAIXO' || !a.nivel_risco).length },
  ];

  // Reincidence analysis (simulated - would need real data)
  const reincidenceData = [
    { area: 'Licitações', taxa: 35 },
    { area: 'Contratos', taxa: 28 },
    { area: 'Pessoal', taxa: 15 },
    { area: 'Patrimônio', taxa: 12 },
    { area: 'Outros', taxa: 10 },
  ];

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 space-y-6" data-testid="cge-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard CGE</h1>
            <p className="text-slate-600">Controladoria-Geral do Estado - Visão de Compliance</p>
          </div>
          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
            <Eye className="w-4 h-4 mr-1" />
            CGE
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="risk">Riscos</TabsTrigger>
            <TabsTrigger value="audit">Auditoria</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Taxa de Conformidade</p>
                      <p className="text-3xl font-bold">{complianceRate}%</p>
                    </div>
                    <Shield className="w-10 h-10 text-blue-200" />
                  </div>
                  <Progress value={complianceRate} className="mt-3 bg-blue-400" />
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Exposição a Risco</p>
                      <p className="text-2xl font-bold text-red-600">{riskExposure}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-200" />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Atos críticos/atrasados</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Auditorias Pendentes</p>
                      <p className="text-2xl font-bold text-blue-600">{pendingAudits}</p>
                    </div>
                    <FileSearch className="w-8 h-8 text-blue-200" />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Em análise/andamento</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Atos no Prazo</p>
                      <p className="text-2xl font-bold text-green-600">{stats?.em_andamento || 0}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-200" />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Dentro do SLA</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trend Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    Evolução Mensal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={monthlyTrend}>
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="recebidos" fill={COLORS.primary} name="Recebidos" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="concluidos" stroke={COLORS.success} strokeWidth={2} name="Concluídos" />
                      <Line type="monotone" dataKey="atrasados" stroke={COLORS.danger} strokeWidth={2} name="Atrasados" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Origin Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Atos por Órgão de Origem
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={originData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) => `${name.substring(0, 10)} ${(percent * 100).toFixed(0)}%`}
                      >
                        {originData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % 5]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="compliance" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Compliance by Area */}
              <Card>
                <CardHeader>
                  <CardTitle>Conformidade por Área</CardTitle>
                  <CardDescription>Taxa de atendimento de prazos por área temática</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['Licitações', 'Contratos', 'Pessoal', 'Patrimônio', 'Finanças'].map((area, i) => {
                      const rate = Math.round(70 + Math.random() * 25);
                      return (
                        <div key={area}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-600">{area}</span>
                            <span className="font-medium">{rate}%</span>
                          </div>
                          <Progress value={rate} className={rate < 80 ? 'bg-amber-100' : 'bg-green-100'} />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Compliance Issues */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-600">
                    <FileWarning className="w-5 h-5" />
                    Alertas de Compliance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {acts.filter(a => a.status === 'ATRASADO').slice(0, 5).map((act, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100">
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{act.numero_ato}</p>
                          <p className="text-xs text-slate-600">{act.tipo_ato}</p>
                        </div>
                        <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                          Atrasado
                        </Badge>
                      </div>
                    ))}
                    {acts.filter(a => a.status === 'ATRASADO').length === 0 && (
                      <p className="text-slate-500 text-center py-4">Nenhum alerta no momento</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="risk" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Risk Matrix */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-red-600" />
                    Matriz de Risco
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={riskMatrix} layout="vertical">
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="risco" width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {riskMatrix.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={['#EF4444', '#F59E0B', '#3B82F6', '#10B981'][index]} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Reincidence Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Análise de Reincidência
                  </CardTitle>
                  <CardDescription>Áreas com maior taxa de recorrência de irregularidades</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={reincidenceData}>
                      <XAxis dataKey="area" />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="taxa" fill={COLORS.primary} radius={[4, 4, 0, 0]} name="Taxa %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSearch className="w-5 h-5 text-blue-600" />
                  Log de Auditoria Recente
                </CardTitle>
                <CardDescription>Últimas ações registradas no sistema</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {auditLogs.slice(0, 10).map((log, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          log.action === 'CREATE' ? 'bg-green-500' :
                          log.action === 'UPDATE' ? 'bg-blue-500' :
                          log.action === 'DELETE' ? 'bg-red-500' : 'bg-slate-400'
                        }`} />
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{log.action} - {log.entity_type}</p>
                          <p className="text-xs text-slate-500">{log.user_email} • {new Date(log.timestamp).toLocaleString('pt-BR')}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">{log.entity_id?.substring(0, 8)}...</Badge>
                    </div>
                  ))}
                  {auditLogs.length === 0 && (
                    <p className="text-slate-500 text-center py-8">Nenhum log de auditoria encontrado</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default CGEDashboard;
